import {
  BubbleButton,
  BubbleFormGroup,
  BubbleHintSelect,
  BubbleInput,
} from '@go-client/components/BubbleUI';
import { mapBy } from '@go-shared/lib/functional';
import { addMinutesToTime, timeCompare } from '@go-shared/lib/time';
import { safeIdCompare } from '@go-shared/lib/utils';
import { BareVenueTemplate } from '@go-shared/types/venueTemplate';
import { ObjectId } from 'bson';
import { FormikProps } from 'formik';
import React from 'react';
import { useBubbleDrawerTools } from '../BubbleUI/BubbleDrawer';
import { ReservableScheduleFormikForm } from '../Formik/ReservableScheduleFormik';

interface ReservableCommunicationsProps {
  readOnly?: boolean;
  disabled?: boolean;
  formik: FormikProps<ReservableScheduleFormikForm>;
  ruleIdx: number;
  venueTemplates: BareVenueTemplate[];
}

export function ReservableCommunications(props: ReservableCommunicationsProps) {
  const { readOnly, disabled, formik, venueTemplates } = props;

  const { scrollToBottom } = useBubbleDrawerTools();
  const venuesDict = mapBy(venueTemplates, vt => vt._id);

  const { values, setFieldValue, handleBlur } = formik;
  const rule = values.rules[props.ruleIdx];

  return (
    <fieldset
      disabled={readOnly}
      className="bg-white p-4 border rounded shadow grid grid-cols-1 gap-4 justify-items-start"
    >
      {!readOnly && (
        <div className="text-xs text-slate italic">
          Set how often this event appears on the schedule. Template visibility rules still apply.
        </div>
      )}
      {(rule.communications || []).map((comms, i) => (
        <div key={i} className="w-full grid grid-cols-3 gap-4">
          <BubbleFormGroup label="Start Time" className="flex-grow">
            <BubbleInput
              type="time"
              block
              name={`rules[${props.ruleIdx}].communications[${i}].time`}
              value={comms.time}
              onValue={val => {
                if (!val) return;
                setFieldValue(`rules[${props.ruleIdx}].communications[${i}].time`, val);
              }}
              onBlur={handleBlur}
              readOnly={readOnly}
            />
          </BubbleFormGroup>
          <BubbleFormGroup label="End Time" className="flex-grow">
            <BubbleInput
              type="time"
              block
              name={`rules[${props.ruleIdx}].communications[${i}].duration`}
              value={addMinutesToTime(comms.time, comms.duration)}
              onValue={val => {
                if (!val) return;
                const duration = timeCompare(val, comms.time);
                setFieldValue(`rules[${props.ruleIdx}].communications[${i}].duration`, duration);
              }}
              onBlur={handleBlur}
              readOnly={readOnly}
            />
          </BubbleFormGroup>
          <div className="flex-grow flex items-center">
            <BubbleFormGroup label="Venue" className="flex-grow" asterisk>
              <BubbleHintSelect
                block
                value={venuesDict[comms.venue]}
                allowUndefined={false}
                options={venueTemplates.filter(
                  vt => safeIdCompare(vt._id, comms.venue) || !vt.archived
                )}
                onValue={val => {
                  if (!val) return;
                  setFieldValue(`rules[${props.ruleIdx}].communications[${i}].venue`, val._id);
                }}
                labelExtractor={vt => vt.name}
                internalValueExtractor={vt => vt._id}
                menuClassName="max-h-80 overflow-y-auto"
                onBlur={handleBlur}
                readOnly={readOnly}
              />
            </BubbleFormGroup>
            {!readOnly && (
              <BubbleButton
                className="-mr-3"
                style={{ alignSelf: 'end' }}
                variant="crimson"
                inverse
                icon="trash-alt"
                disabled={disabled}
                onClick={() => {
                  const newCommunications = [...rule.communications];
                  newCommunications.splice(i, 1);
                  setFieldValue(`rules[${props.ruleIdx}].communications`, newCommunications);
                }}
              />
            )}
          </div>
        </div>
      ))}
      {readOnly && !rule.communications.length && <span className="text-smoke italic">None</span>}
      {!readOnly && (
        <>
          <BubbleButton
            variant="navy"
            inverse
            className="p-0-i"
            icon="plus"
            disabled={disabled}
            onClick={() => {
              setFieldValue(`rules[${props.ruleIdx}].communications`, [
                ...(rule.communications || []),
                {
                  _id: new ObjectId().toHexString(),
                  time: {
                    hour: 9,
                    minute: 0,
                  },
                  venue: venueTemplates[0]?._id || '',
                  duration: 60,
                },
              ]);
              scrollToBottom();
            }}
          >
            Add Communications
          </BubbleButton>
        </>
      )}
    </fieldset>
  );
}
